/**
 * How each collection shows its fields, where the default is wrong.
 *
 * Keyed by collection, then by the path the searchable surface publishes. A
 * path not listed here is labelled by `labelFor` and offered in the filter panel
 * and the typeahead. An entry changes any of these:
 *
 * - `label` — what the screen calls the field, when `labelFor` reads the path
 *   badly. A nested path names its containers as well as its leaf, so
 *   `operationInfo.legalCompany.companyId` would read "Operation info legal
 *   company company id".
 * - `searchHidden` — left out of the filter panel and the typeahead. The field
 *   stays in the surface, so a query that names it still works; a roster's link
 *   to `operationInfo.operationId:…` or `_id:…` depends on that. Identifiers are
 *   hidden because nobody types one, and each sits beside a name that finds the
 *   same record.
 * - `identifier` — left out of a record's detail groups as well. The value is
 *   still saved with the group; it is only not drawn, because a hex key or a
 *   Stripe customer key means nothing to somebody reading billing facts.
 */
export const FIELD_META = {
    'companies': {
        '_id': { label: 'ID', searchHidden: true },
        'billing.address.addressLine1': { label: 'Billing address line 1', searchHidden: true },
        'billing.contact.humanId': { searchHidden: true, identifier: true },
        'billing.taxId': { searchHidden: true },
        'billing.stripeCustomerId': { searchHidden: true, identifier: true }
    },
    'auction-operations': {
        '_id': { label: 'ID', searchHidden: true },
        'legalCompany.companyId': { label: 'Legal company ID', searchHidden: true },
        'billingInfo.companyInfo.companyId': { label: 'Billing company ID', searchHidden: true },
        'billingInfo.companyInfo.name': { label: 'Billing company name' },
        'billingInfo.stripeSubscriptionId': { label: 'Billing Stripe subscription ID', searchHidden: true },
        'billingInfo.standing': { label: 'Billing standing' },
        'managementCompany.companyId': { label: 'Management company ID', searchHidden: true },
        'knockedOutHumans.humanId': { label: 'Knocked out human ID', searchHidden: true },
        'knockedOutHumans.name': { label: 'Knocked out human name' },
        'knockedOutDealerships.dealershipId': { label: 'Knocked out dealership ID', searchHidden: true },
        'knockedOutDealerships.name': { label: 'Knocked out dealership name' }
    },
    'dealership-operations': {
        '_id': { label: 'ID', searchHidden: true },
        'legalCompany.companyId': { label: 'Legal company ID', searchHidden: true },
        'billingInfo.companyInfo.companyId': { label: 'Billing company ID', searchHidden: true },
        'billingInfo.companyInfo.name': { label: 'Billing company name' },
        'billingInfo.stripeSubscriptionId': { label: 'Billing Stripe subscription ID', searchHidden: true },
        'billingInfo.standing': { label: 'Billing standing' },
        'managementCompany.companyId': { label: 'Management company ID', searchHidden: true }
    },
    'auction-houses': {
        '_id': { label: 'ID', searchHidden: true },
        'operationInfo.operationId': { label: 'Operation ID', searchHidden: true },
        'operationInfo.name': { label: 'Operation name' },
        'operationInfo.status': { label: 'Operation status' },
        'operationInfo.legalCompany.companyId': { label: 'Operation legal company ID', searchHidden: true },
        'operationInfo.legalCompany.name': { label: 'Operation legal company name' },
        'operationInfo.billingInfo.companyInfo.companyId': { label: 'Operation billing company ID', searchHidden: true },
        'operationInfo.billingInfo.companyInfo.name': { label: 'Operation billing company name' },
        'operationInfo.billingInfo.stripeSubscriptionId': { label: 'Operation billing Stripe subscription ID', searchHidden: true },
        'operationInfo.billingInfo.standing': { label: 'Operation billing standing' },
        'operationInfo.managementCompany.companyId': { label: 'Operation management company ID', searchHidden: true },
        'operationInfo.managementCompany.name': { label: 'Operation management company name' },
        'legalCompany.companyId': { label: 'Legal company ID', searchHidden: true },
        'billingInfo.companyInfo.companyId': { label: 'Billing company ID', searchHidden: true },
        'billingInfo.companyInfo.name': { label: 'Billing company name' },
        'billingInfo.stripeSubscriptionId': { label: 'Billing Stripe subscription ID', searchHidden: true },
        'billingInfo.standing': { label: 'Billing standing' },
        'location.addressLine1': { searchHidden: true },
        'knockedOutHumans.humanId': { label: 'Knocked out human ID', searchHidden: true },
        'knockedOutHumans.name': { label: 'Knocked out human name' },
        'registeredDealerships.dealershipId': { label: 'Registered dealership ID', searchHidden: true },
        'registeredDealerships.name': { label: 'Registered dealership name' },
        'registeredDealerships.isKnockedOut': { label: 'Registered dealership knocked out' }
    },
    'dealerships': {
        '_id': { label: 'ID', searchHidden: true },
        'operationInfo.operationId': { label: 'Operation ID', searchHidden: true },
        'operationInfo.name': { label: 'Operation name' },
        'operationInfo.status': { label: 'Operation status' },
        'operationInfo.legalCompany.companyId': { label: 'Operation legal company ID', searchHidden: true },
        'operationInfo.legalCompany.name': { label: 'Operation legal company name' },
        'operationInfo.billingInfo.companyInfo.companyId': { label: 'Operation billing company ID', searchHidden: true },
        'operationInfo.billingInfo.companyInfo.name': { label: 'Operation billing company name' },
        'operationInfo.billingInfo.stripeSubscriptionId': { label: 'Operation billing Stripe subscription ID', searchHidden: true },
        'operationInfo.billingInfo.standing': { label: 'Operation billing standing' },
        'operationInfo.managementCompany.companyId': { label: 'Operation management company ID', searchHidden: true },
        'operationInfo.managementCompany.name': { label: 'Operation management company name' },
        'legalCompany.companyId': { label: 'Legal company ID', searchHidden: true },
        'billingInfo.companyInfo.companyId': { label: 'Billing company ID', searchHidden: true },
        'billingInfo.companyInfo.name': { label: 'Billing company name' },
        'billingInfo.stripeSubscriptionId': { label: 'Billing Stripe subscription ID', searchHidden: true },
        'billingInfo.standing': { label: 'Billing standing' },
        'location.addressLine1': { searchHidden: true },
        'claims.claimType': { label: 'Claim type' }
    }
}
